/**
 * Data processor for the CRM dashboard pilote (`/crm/dashboard`).
 *
 * Self-matches by URL and shapes each Elasticsearch aggregation envelope into
 * ready-to-render Chart.js datasets + KPIs, so the page renders 100 %
 * declaratively (ChartJs primitive + DS `.stat-card` recipes) — no presentational
 * React. Two sources feed the page :
 *   - `/config/deals_list`          → graphe A (CA réalisé/an) + KPIs
 *   - `/config/pipeline_transitions` → graphe B (transitions par étape/mois, empilé)
 *
 * Le reshaping des buckets ES est délégué au helper générique `esAggToChart`.
 */

import { singleSeries, stackedByTerms, dsPalette } from './esAggToChart';

const EUR = new Intl.NumberFormat('fr-FR', {
  style: 'currency', currency: 'EUR', maximumFractionDigits: 0,
});
const MONTH = new Intl.DateTimeFormat('fr-FR', { month: 'short', year: '2-digit' });

const yearLabel = (b) => String(b.key_as_string ?? '').slice(0, 4);
const monthLabel = (b) => {
  const iso = b.key_as_string ?? '';
  const d = iso ? new Date(iso) : null;
  return d && !Number.isNaN(d.getTime()) ? MONTH.format(d) : iso.slice(0, 7);
};

/** Graphe A + KPIs depuis l'enveloppe `deals_list`. */
function shapeDeals(data) {
  const agg = data.aggregations ?? {};
  const years = agg.revenue_by_year?.per_year?.buckets ?? [];
  const palette = dsPalette();

  const revenueByYear = singleSeries(years, {
    labelFor: yearLabel,
    valueFor: (b) => Math.round(b.revenue?.value ?? 0),
    label: 'CA réalisé',
    color: palette(0),
  });

  const wonCount = years.reduce((s, b) => s + (b.doc_count ?? 0), 0);
  const wonRevenue = years.reduce((s, b) => s + (b.revenue?.value ?? 0), 0);
  const closedBuckets = agg.by_closed?.buckets ?? [];
  const totalDeals = data.pagination?.total
    ?? closedBuckets.reduce((s, b) => s + (b.doc_count ?? 0), 0);
  const wonRate = totalDeals ? Math.round((wonCount / totalDeals) * 100) : 0;

  return {
    ...data,
    charts: { revenueByYear },
    kpis: {
      totalDeals,
      totalDealsFormatted: totalDeals.toLocaleString('fr-FR'),
      wonRevenueFormatted: EUR.format(wonRevenue),
      wonRate,
      wonRateFormatted: `${wonRate} %`,
    },
    flags: { hasRevenue: revenueByYear.labels.length > 0 },
    present: { revenue: revenueByYear.labels.length ? [{}] : [] },
    absent: { revenue: revenueByYear.labels.length ? [] : [{}] },
  };
}

/** Graphe B (empilé) depuis l'enveloppe `pipeline_transitions`. */
function shapeTransitions(data) {
  const agg = data.aggregations ?? {};
  const months = agg.per_month?.buckets ?? [];
  const stageLabels = agg.by_stage_labels ?? {};

  const byStagePerMonth = stackedByTerms(months, {
    innerKey: 'by_stage',
    labelFor: monthLabel,
    seriesLabels: stageLabels,
  });

  const hasData = byStagePerMonth.labels.length > 0 && byStagePerMonth.datasets.length > 0;
  return {
    ...data,
    charts: { byStagePerMonth },
    flags: { hasStages: hasData },
    present: { stages: hasData ? [{}] : [] },
    absent: { stages: hasData ? [] : [{}] },
  };
}

/**
 * Graphe B — instantané mensuel (empilé) depuis `pipeline_snapshot`.
 * L'agg `filters` renvoie des buckets nommés s0..s5 (6 derniers mois, du plus
 * ancien au courant) ; chaque bucket = les deals OUVERTS dans chaque étape à la
 * fin du mois (as-of), y compris ceux qui n'ont pas changé d'étape. Les libellés
 * de mois sont calculés côté client (le serveur et le client s'accordent au mois).
 */
function shapeSnapshot(data) {
  const agg = data.aggregations ?? {};
  const snap = agg.snapshots?.buckets ?? {};
  const stageLabels = agg.by_stage_labels ?? {};
  const now = new Date();
  const rows = ['s0', 's1', 's2', 's3', 's4', 's5'].map((k, i) => ({
    ...(snap[k] ?? { by_stage: { buckets: [] } }),
    _month: MONTH.format(new Date(now.getFullYear(), now.getMonth() - (5 - i), 1)),
  }));

  const byStagePerMonth = stackedByTerms(rows, {
    innerKey: 'by_stage',
    labelFor: (r) => r._month,
    seriesLabels: stageLabels,
  });
  // Variante « montant » : somme des montants des deals ouverts par étape. Via
  // l'index `deal_values` (requête pipeline_amount_snapshot), c'est le montant
  // HISTORISÉ (celui d'époque à chaque instantané), pas le montant courant.
  const byStageAmountPerMonth = stackedByTerms(rows, {
    innerKey: 'by_stage',
    labelFor: (r) => r._month,
    seriesLabels: stageLabels,
    getValue: (s) => Math.round(s.sum_amount?.value ?? 0),
  });

  const hasData = byStagePerMonth.labels.length > 0 && byStagePerMonth.datasets.length > 0;
  return {
    ...data,
    charts: { byStagePerMonth, byStageAmountPerMonth },
    flags: { hasStages: hasData },
    present: { stages: hasData ? [{}] : [] },
    absent: { stages: hasData ? [] : [{}] },
  };
}

export const crmDashboardProcessor = ({ requestContext, responseContext, dataToProcess }) => {
  const url = requestContext?.url ?? '';
  if (responseContext?.status >= 400) return dataToProcess;
  if (!dataToProcess || typeof dataToProcess !== 'object') return dataToProcess;

  if (url.includes('/config/deals_list')) return shapeDeals(dataToProcess);
  // `pipeline_amount_snapshot` (montant historisé, index deal_values) partage la
  // même enveloppe que `pipeline_snapshot` → même shaper (byStageAmountPerMonth).
  // À tester AVANT `pipeline_snapshot` : « pipeline_snapshot » n'est PAS un
  // sous-texte de « pipeline_amount_snapshot », mais on garde l'ordre explicite.
  if (url.includes('/config/pipeline_amount_snapshot')) return shapeSnapshot(dataToProcess);
  if (url.includes('/config/pipeline_snapshot')) return shapeSnapshot(dataToProcess);
  if (url.includes('/config/pipeline_transitions')) return shapeTransitions(dataToProcess);
  return dataToProcess;
};
